import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, arrayMove, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Icon from "@/components/Icon";
import Button from "@/components/Button";
import Skeleton from "@/components/Skeleton";
import { useConfirm } from "@/components/ConfirmDialog";
import { COURSE_COLORS, colorVar } from "@/components/CourseContextBar";
import {
  listMyCourses, updateCourse, duplicateCourse, regenerateCode, deleteCourse,
  listStudents, addStudent, removeStudentsBulk,
  listCourseClasses, assignClass, unassignClass, type CourseClassOption,
} from "@/api/course";
import {
  listInstructorExercises, listChapters, reorderChapters, updateChapter, updateExercise,
} from "@/api/exercises";
import { listPacks, createPack, importBundle, downloadCourseBundle, type ImportBundle, type ConflictStrategy } from "@/api/packs";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/Toast";
import type { Course, CourseStudent, InstructorExercise, Chapter, GamePack } from "@/types";

type Tab = "contenu" | "parametres" | "eleves";
const TABS: { key: Tab; label: string }[] = [
  { key: "contenu", label: "Contenu" },
  { key: "parametres", label: "Paramètres" },
  { key: "eleves", label: "Élèves" },
];

/** Page de gestion d'un cours (URL dédiée) : réglages (titre, couleur, statut), code
 *  d'inscription, classes assignées et roster — chacun dans son bloc, avec une liste
 *  d'élèves scrollable pensée pour les grands effectifs (85-100). */
export default function CourseDetail() {
  const { courseId } = useParams<{ courseId: string }>();
  const id = Number(courseId);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [course, setCourse] = useState<Course | null | undefined>(undefined); // undefined = chargement
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<Tab>("contenu");
  // Compteur incrémenté à chaque import/création — sert de clé de refresh aux
  // sections enfants (ContentTab, PacksSection) qui se rechargent sans reload.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((n) => n + 1);

  const reload = () =>
    listMyCourses()
      .then((cs) => { const c = cs.find((x) => x.id === id) ?? null; setCourse(c); if (c) setTitle(c.title); })
      .catch(() => setCourse(null));
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const guard = async (p: Promise<unknown>) => {
    setError(null);
    try { await p; await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : "Erreur."); }
  };

  const commitTitle = () => {
    const v = title.trim();
    if (course && v && v !== course.title) guard(updateCourse(id, { title: v }));
    else if (course) setTitle(course.title);
  };

  const duplicate = async () => {
    setBusy(true); setError(null);
    try { const c = await duplicateCourse(id); navigate(`/instructor/courses/${c.id}`); }
    catch (e) { setError(e instanceof Error ? e.message : "Échec de la duplication."); setBusy(false); }
  };

  const archive = async () => {
    if (!course) return;
    const ok = course.archived || await confirm({
      title: `Archiver « ${course.title} » ?`,
      message: "Le cours et sa progression sont conservés, mais il sort des vues actives.",
      confirmLabel: "Archiver",
    });
    if (ok) guard(updateCourse(id, { archived: !course.archived }));
  };

  const remove = async () => {
    if (!course) return;
    const ok = await confirm({
      title: `Supprimer définitivement « ${course.title} » ?`,
      message: "Le cours, son contenu (séries, exercices) et la progression associée seront "
             + "définitivement supprimés. Cette action est irréversible.",
      confirmLabel: "Supprimer définitivement",
      danger: true,
    });
    if (!ok) return;
    setError(null);
    try { await deleteCourse(id); navigate("/instructor/archives"); }
    catch (e) { setError(e instanceof Error ? e.message : "Échec de la suppression."); }
  };

  if (course === undefined) {
    return <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 820 }}>
      <Skeleton height={32} width="40%" /><Skeleton height={120} /><Skeleton height={160} />
    </div>;
  }
  if (course === null) {
    return (
      <div>
        <Link to="/instructor/courses" style={styles.back}><Icon name="back" size={16} /> Retour aux cours</Link>
        <p style={styles.empty}>Cours introuvable.</p>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <style>{`
        .pl-serie-row:hover { background: var(--bg-bar); }
        .pl-serie-row:focus-visible { outline: 2px solid var(--blue); outline-offset: -2px; }
      `}</style>
      <Link to={course.archived ? "/instructor/archives" : "/instructor/courses"} style={styles.back}>
        <Icon name="back" size={16} /> {course.archived ? "Retour aux archives" : "Retour aux cours"}
      </Link>

      {error && <div style={styles.error}><Icon name="alert" size={15} style={{ color: "var(--red)", flexShrink: 0 }} /> {error}</div>}

      {/* En-tête commun : titre éditable + statut */}
      <div style={styles.header}>
        <span style={{ ...styles.dot, background: colorVar(course.color) }} />
        <input style={styles.titleInput} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={commitTitle}
               onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} aria-label="Titre du cours" />
        <span style={{ ...styles.status, ...(course.archived ? styles.statusArchived : styles.statusActive) }}>
          {course.archived ? "Archivé" : "Actif"}
        </span>
      </div>

      {/* Onglets */}
      <div style={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} type="button"
                  style={{ ...styles.tabBtn, ...(tab === t.key ? styles.tabBtnOn : {}) }}
                  onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "contenu" && (
        <>
          <ContentTab courseId={id} refreshKey={refreshKey} onEdit={() => navigate(`/instructor/c/${id}`)} />
          <BundleImport courseId={id} onImported={bumpRefresh} />
          <PacksSection courseId={id} refreshKey={refreshKey} setError={setError} onChanged={bumpRefresh} />
        </>
      )}

      {tab === "parametres" && (
        <>
          <section style={styles.block}>
            <span style={styles.label}>Couleur</span>
            <div style={styles.colorRow}>
              {COURSE_COLORS.map((col) => (
                <button key={col} type="button" onClick={() => guard(updateCourse(id, { color: col }))}
                        aria-label={`Couleur ${col}`} title={col}
                        style={{ ...styles.swatch, background: colorVar(col), outline: col === course.color ? "2px solid var(--text)" : "none" }} />
              ))}
            </div>
          </section>

          <section style={styles.block}>
            <span style={styles.label}>Code d'inscription</span>
            <div style={styles.codeLine}>
              <span style={styles.codeBig}>{course.join_code}</span>
              <button style={styles.smallBtn} onClick={() => guard(regenerateCode(id))} type="button"><Icon name="duplicate" size={12} /> Régénérer</button>
              <span style={styles.hint}>À projeter — les élèves le saisissent pour rejoindre.</span>
            </div>
          </section>

          <Classes courseId={id} setError={setError} onChanged={reload} />

          <section style={styles.block}>
            <span style={styles.label}>Actions</span>
            <div style={styles.actionsRow}>
              <Button variant="secondary" size="sm" onClick={duplicate} disabled={busy}>
                {busy ? <span className="spin" /> : <><Icon name="duplicate" size={14} /> Dupliquer (rentrée)</>}
              </Button>
              <Button variant="secondary" size="sm" onClick={archive}>
                <Icon name={course.archived ? "up" : "archive"} size={14} /> {course.archived ? "Désarchiver" : "Archiver"}
              </Button>
            </div>
            {/* Suppression définitive : seulement pour un cours archivé (cohérent avec le garde-fou API). */}
            {course.archived && (
              <div style={styles.dangerZone}>
                <div>
                  <div style={styles.dangerTitle}>Supprimer définitivement</div>
                  <div style={styles.dangerHint}>Le cours et tout son contenu seront effacés. Irréversible.</div>
                </div>
                <button style={styles.dangerBtn} type="button" onClick={remove}>
                  <Icon name="trash" size={14} /> Supprimer
                </button>
              </div>
            )}
          </section>
        </>
      )}

      {tab === "eleves" && <Roster courseId={id} setError={setError} />}
    </div>
  );
}

/** Une série du cockpit : œil (publier/masquer) + ligne cliquable vers l'éditeur. */
function SerieRow({ s, courseId, onToggle }:
                  { s: InstructorExercise; courseId: number; onToggle: (s: InstructorExercise) => void }) {
  const navigate = useNavigate();
  const hidden = !s.published;
  return (
    <div style={{ ...styles.serieRow, ...(hidden ? styles.rowHidden : {}) }}>
      <button type="button" style={styles.eyeBtn} onClick={() => onToggle(s)}
              aria-label={hidden ? "Publier (visible des élèves)" : "Masquer (brouillon)"}
              title={hidden ? "Masqué des élèves — cliquer pour publier" : "Visible des élèves — cliquer pour masquer"}>
        <Icon name={hidden ? "eye-off" : "eye"} size={15} />
      </button>
      <button type="button" className="pl-serie-row" style={styles.serieNav}
              onClick={() => navigate(`/instructor/c/${courseId}?serie=${s.id}`)}
              title="Modifier cette série">
        <span style={styles.serieTitle}>{s.title}</span>
        {hidden && <span style={styles.draftBadge}>brouillon</span>}
        <span style={{ flex: 1 }} />
        <span style={styles.serieMeta}>{s.step_count ?? 0} exercice{(s.step_count ?? 0) > 1 ? "s" : ""}</span>
        <Icon name="next" size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      </button>
    </div>
  );
}

/** Bloc chapitre draggable : poignée + numéro + titre éditable en place + œil + séries. */
function ChapterBlock({
  chapter, number, series, courseId, onRename, onToggleVisible, onToggleSerie,
}: {
  chapter: Chapter;
  number: number;
  series: InstructorExercise[];
  courseId: number;
  onRename: (id: number, title: string) => void;
  onToggleVisible: (c: Chapter) => void;
  onToggleSerie: (s: InstructorExercise) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: chapter.id });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(chapter.title);
  const hidden = chapter.visible === false;

  const commit = () => {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== chapter.title) onRename(chapter.id, t);
    else setDraft(chapter.title);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ ...styles.chapterRow, ...(hidden ? styles.rowHidden : {}) }}>
        <button style={styles.chHandle} {...attributes} {...listeners} aria-label="Déplacer le chapitre" type="button">
          <DragDots />
        </button>
        <span style={styles.chNum}>{number}</span>
        {editing ? (
          <input autoFocus style={styles.chTitleInput} value={draft}
                 onChange={(e) => setDraft(e.target.value)} onBlur={commit}
                 onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(chapter.title); setEditing(false); } }} />
        ) : (
          <span style={styles.chTitleText} onClick={() => setEditing(true)} title="Cliquer pour renommer">
            {chapter.title}
          </span>
        )}
        <span style={styles.chapterCount}>{series.length} série{series.length > 1 ? "s" : ""}</span>
        <button type="button" style={styles.eyeBtn} onClick={() => onToggleVisible(chapter)}
                aria-label={hidden ? "Afficher le chapitre" : "Masquer le chapitre"}
                title={hidden ? "Chapitre masqué des élèves — cliquer pour afficher" : "Chapitre visible — cliquer pour masquer"}>
          <Icon name={hidden ? "eye-off" : "eye"} size={16} />
        </button>
      </div>
      <div style={styles.serieList}>
        {series.length === 0
          ? <p style={styles.chEmpty}>Aucune série dans ce chapitre.</p>
          : series.map((s) => <SerieRow key={s.id} s={s} courseId={courseId} onToggle={onToggleSerie} />)}
      </div>
    </div>
  );
}

/** Onglet « Contenu » : cockpit d'organisation du cours — réordonner les chapitres
 *  (glisser, numéros auto), les renommer en place, et afficher/masquer chapitres &
 *  séries pour révéler le contenu au fur et à mesure. `refreshKey` force un refetch. */
function ContentTab({ courseId, refreshKey, onEdit }:
                    { courseId: number; refreshKey: number; onEdit: () => void }) {
  const [series, setSeries] = useState<InstructorExercise[] | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  useEffect(() => {
    listInstructorExercises(courseId).then(setSeries).catch(() => setSeries([]));
    listChapters(courseId).then(setChapters).catch(() => {});
  }, [courseId, refreshKey]);

  const chaptersSorted = useMemo(
    () => [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [chapters],
  );
  // Séries rattachées par chapitre (M2M) + bucket « Sans chapitre ».
  const { byChapter, noChapter } = useMemo(() => {
    const byChapter = new Map<number, InstructorExercise[]>();
    const noChapter: InstructorExercise[] = [];
    for (const s of series ?? []) {
      if (!s.chapters || s.chapters.length === 0) noChapter.push(s);
      else for (const c of s.chapters) {
        const arr = byChapter.get(c.id) ?? [];
        arr.push(s); byChapter.set(c.id, arr);
      }
    }
    return { byChapter, noChapter };
  }, [series]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = chaptersSorted.findIndex((c) => c.id === active.id);
    const newIdx = chaptersSorted.findIndex((c) => c.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const moved = arrayMove(chaptersSorted, oldIdx, newIdx);
    setChapters(moved.map((c, i) => ({ ...c, order: i }))); // renumérotation optimiste
    reorderChapters(courseId, moved.map((c) => c.id))
      .catch(() => listChapters(courseId).then(setChapters));
  };

  const renameChapter = (id: number, title: string) => {
    setChapters((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
    updateChapter(id, { title }).catch(() => listChapters(courseId).then(setChapters));
  };
  const toggleChapter = (c: Chapter) => {
    const visible = c.visible === false; // bascule
    setChapters((prev) => prev.map((x) => (x.id === c.id ? { ...x, visible } : x)));
    updateChapter(c.id, { visible }).catch(() => listChapters(courseId).then(setChapters));
  };
  const toggleSerie = (s: InstructorExercise) => {
    const published = !s.published;
    setSeries((prev) => (prev ?? []).map((x) => (x.id === s.id ? { ...x, published } : x)));
    updateExercise(s.id, { published }).catch(() => listInstructorExercises(courseId).then(setSeries));
  };

  const empty = series !== null && series.length === 0 && chapters.length === 0;

  return (
    <section style={styles.block}>
      <div style={styles.contentHead}>
        <span style={styles.label}>Contenu du cours{series ? ` — ${series.length} série${series.length > 1 ? "s" : ""}` : ""}</span>
        <Button variant="primary" size="sm" onClick={onEdit}>
          <Icon name="rename" size={14} /> Modifier le contenu
        </Button>
      </div>
      <p style={styles.cockpitHint}>
        Glisse <span style={{ opacity: 0.6 }}>⠿</span> pour réordonner les chapitres · clique un titre pour le renommer ·
        l'œil affiche / masque un chapitre ou une série aux élèves.
      </p>

      {series === null ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={38} />)}
        </div>
      ) : empty ? (
        <p style={styles.empty}>Aucun contenu. Clique sur « Modifier le contenu » pour créer ta première série.</p>
      ) : (
        <div style={styles.groupList}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={chaptersSorted.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {chaptersSorted.map((c, i) => (
                <ChapterBlock
                  key={c.id}
                  chapter={c}
                  number={i + 1}
                  series={byChapter.get(c.id) ?? []}
                  courseId={courseId}
                  onRename={renameChapter}
                  onToggleVisible={toggleChapter}
                  onToggleSerie={toggleSerie}
                />
              ))}
            </SortableContext>
          </DndContext>

          {noChapter.length > 0 && (
            <div>
              <div style={styles.chapterHead}>
                <span style={styles.chapterTitle}>Sans chapitre</span>
                <span style={styles.chapterCount}>{noChapter.length} série{noChapter.length > 1 ? "s" : ""}</span>
              </div>
              <div style={styles.serieList}>
                {noChapter.map((s) => <SerieRow key={s.id} s={s} courseId={courseId} onToggle={toggleSerie} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Drag-and-drop d'un fichier JSON conforme au schéma `ImportBundle` pour créer
 *  packs + séries + étapes en une fois. Gateée gamelib (même feature flag que les
 *  packs). Sur conflit (nom déjà pris), le backend renvoie 409 + liste — on les
 *  affiche directement dans la section (pas via le bandeau global en haut de la
 *  page, qui serait hors écran depuis le bas où se trouve le dropzone). */
function BundleImport({
  courseId, onImported,
}: { courseId: number; onImported: () => void }) {
  const { user } = useAuth();
  const toast    = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy,    setBusy]    = useState(false);
  const [dragOn,  setDragOn]  = useState(false);
  // État local affiché en bandeau directement sous le dropzone — l'utilisateur
  // n'a pas à scroller pour voir s'il y a eu un conflit. On garde aussi le
  // bundle parsé du DERNIER drop (pendingBundle) pour pouvoir le ré-envoyer
  // avec une autre stratégie (Remplacer / Renommer) sans re-glisser le fichier.
  type Renamed = { packs: Record<string, string>; series: Record<string, string> };
  type Feedback =
    | { kind: "success"; packs: number; series: number; steps: number;
        file: string; strategy: ConflictStrategy; renamed: Renamed }
    | { kind: "conflict"; items: string[]; file: string }
    | { kind: "errors";   items: string[]; file: string }
    | { kind: "error";    message: string; file: string };
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [pending, setPending]   = useState<{ file: string; bundle: ImportBundle } | null>(null);
  const enabled = Boolean(user?.features?.gamelib);
  if (!enabled) return null;

  const submit = async (bundle: ImportBundle, fileName: string,
                       strategy: ConflictStrategy = "refuse") => {
    setBusy(true);
    try {
      const res = await importBundle(courseId, { ...bundle, conflict_strategy: strategy });
      setFeedback({ kind: "success", file: fileName, strategy: res.strategy,
                    packs: res.packs_created, series: res.series_created,
                    steps: res.steps_created, renamed: res.renamed });
      toast.success(`Importé ${res.series_created} série(s) depuis ${fileName}`);
      setPending(null);
      onImported();
    } catch (e) {
      if (e instanceof ApiError) {
        const conflicts = e.body.conflicts as string[] | undefined;
        const errors    = e.body.errors    as string[] | undefined;
        if (conflicts?.length) {
          setFeedback({ kind: "conflict", file: fileName, items: conflicts });
          setPending({ file: fileName, bundle });
        } else if (errors?.length) {
          setFeedback({ kind: "errors", file: fileName, items: errors });
          setPending(null);
        } else {
          setFeedback({ kind: "error", file: fileName, message: e.message });
          setPending(null);
        }
      } else {
        setFeedback({ kind: "error", file: fileName,
                      message: e instanceof Error ? e.message : String(e) });
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (file: File) => {
    setFeedback(null);
    setPending(null);
    if (!file.name.toLowerCase().endsWith(".json")) {
      setFeedback({ kind: "error", file: file.name,
                    message: "Fichier non supporté : il faut un .json (schéma ImportBundle)." });
      return;
    }
    let parsed: ImportBundle;
    try {
      const text = await file.text();
      parsed = JSON.parse(text) as ImportBundle;
    } catch {
      setFeedback({ kind: "error", file: file.name,
                    message: "JSON mal formé (parse error)." });
      return;
    }
    if (inputRef.current) inputRef.current.value = "";  // permet de re-déposer le même fichier
    await submit(parsed, file.name);
  };

  const retry = (strategy: ConflictStrategy) => {
    if (!pending) return;
    submit(pending.bundle, pending.file, strategy);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOn(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const onExport = async () => {
    try {
      const fn = await downloadCourseBundle(courseId);
      if (fn) toast.success(`Téléchargé : ${fn}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Échec de l'export.");
    }
  };

  return (
    <section style={styles.block}>
      <div style={styles.bundleHead}>
        <span style={styles.label}>Bundle JSON — import / export</span>
        <span style={{ flex: 1 }} />
        <Button variant="secondary" size="sm" onClick={onExport}
                title="Télécharge tout le cours (packs avec assets, séries, étapes) au format ImportBundle.">
          <Icon name="archive" size={13} /> Exporter ce cours
        </Button>
      </div>
      <p style={styles.hint}>
        Dépose un fichier <code>.json</code> au schéma <code>ImportBundle</code> pour créer
        d'un coup packs + séries + étapes. Refuse si un nom est déjà pris (rien n'est écrasé).
        L'export te donne un fichier réimportable identique — utile pour partager une leçon
        ou sauvegarder une version.
      </p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOn(true); }}
        onDragLeave={() => setDragOn(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          ...styles.dropzone,
          ...(dragOn ? styles.dropzoneOn : {}),
          ...(busy   ? styles.dropzoneBusy : {}),
        }}
        role="button"
        aria-label="Déposer un fichier JSON ou cliquer pour parcourir"
      >
        <Icon name="file" size={22} style={{ color: "var(--blue)" }} />
        <span style={styles.dropzoneText}>
          {busy ? "Import en cours…" : dragOn ? "Relâche pour importer" : "Glisse un .json ici, ou clique pour parcourir"}
        </span>
        <input ref={inputRef} type="file" accept="application/json,.json"
               style={{ display: "none" }}
               onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
      </div>

      {/* Bandeau résultat — vert si succès, rouge si conflit/erreur. Toujours
          AFFICHÉ ici (et pas en haut de la page) pour rester visible depuis le
          dropzone, même quand l'utilisateur scrolle. */}
      {feedback?.kind === "success" && (
        <div style={styles.bannerOk}>
          <Icon name="done" size={16} style={{ color: "var(--green)", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={styles.bannerTitle}>Import réussi — {feedback.file}</div>
            <div style={styles.bannerBody}>
              {feedback.packs} pack(s), {feedback.series} série(s), {feedback.steps} étape(s) ajouté(s)
              {feedback.strategy === "replace" && " (mode : remplacement)"}
              {feedback.strategy === "rename"  && " (mode : renommage auto)"}.
              La liste s'est mise à jour plus haut automatiquement.
            </div>
            {(Object.keys(feedback.renamed.packs).length > 0
              || Object.keys(feedback.renamed.series).length > 0) && (
              <div style={styles.bannerBody}>
                Renommages :
                <ul style={styles.bannerList}>
                  {Object.entries(feedback.renamed.packs).map(([from, to]) => (
                    <li key={`p-${from}`}>pack « {from} » → « {to} »</li>
                  ))}
                  {Object.entries(feedback.renamed.series).map(([from, to]) => (
                    <li key={`s-${from}`}>série « {from} » → « {to} »</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      {feedback?.kind === "conflict" && (
        <div style={styles.bannerErr}>
          <Icon name="alert" size={16} style={{ color: "var(--red)", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={styles.bannerTitle}>Conflit — {feedback.file}</div>
            <div style={styles.bannerBody}>
              Aucun écrasement effectué pour l'instant. Tu peux :
              <ul style={styles.bannerList}>
                {feedback.items.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
            <div style={styles.bannerActions}>
              <Button variant="secondary" size="sm" disabled={busy || !pending}
                      onClick={() => retry("rename")}
                      title="Renomme les éléments du bundle avec un suffixe (_2, (2)…) pour cohabiter">
                Renommer (suffixer)
              </Button>
              <Button variant="secondary" size="sm" disabled={busy || !pending}
                      onClick={() => retry("replace")}
                      title="Supprime les éléments existants du cours avant d'écrire ceux du bundle">
                Remplacer les existants
              </Button>
              <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => { setFeedback(null); setPending(null); }}>
                Annuler
              </Button>
            </div>
          </div>
        </div>
      )}
      {feedback?.kind === "errors" && (
        <div style={styles.bannerErr}>
          <Icon name="alert" size={16} style={{ color: "var(--red)", flexShrink: 0 }} />
          <div>
            <div style={styles.bannerTitle}>Erreurs de validation — {feedback.file}</div>
            <ul style={styles.bannerList}>
              {feedback.items.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        </div>
      )}
      {feedback?.kind === "error" && (
        <div style={styles.bannerErr}>
          <Icon name="alert" size={16} style={{ color: "var(--red)", flexShrink: 0 }} />
          <div>
            <div style={styles.bannerTitle}>Échec — {feedback.file}</div>
            <div style={styles.bannerBody}>{feedback.message}</div>
          </div>
        </div>
      )}
    </section>
  );
}


/** Packs de jeu (gamelib P1.5). Affiché uniquement aux comptes flaggés `gamelib`
 *  (cf. plan P0). Sinon : section invisible — pas de bruit pour les profs hors pilote.
 *  `refreshKey` est incrémenté par le parent après un import bundle → on refetch. */
function PacksSection({ courseId, refreshKey, setError, onChanged }:
                      { courseId: number; refreshKey: number;
                        setError: (s: string | null) => void; onChanged: () => void }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const enabled = Boolean(user?.features?.gamelib);
  const [packs, setPacks] = useState<GamePack[] | null>(null);
  const [name, setName]   = useState("");

  useEffect(() => {
    if (!enabled) return;
    listPacks(courseId).then(setPacks).catch(() => setPacks([]));
  }, [courseId, enabled, refreshKey]);

  if (!enabled) return null;

  const create = async () => {
    const v = name.trim();
    if (!v) return;
    setError(null);
    try {
      const p = await createPack(courseId, v);
      setName("");
      onChanged();
      // Ouvre directement l'éditeur du nouveau pack — gain de temps net.
      navigate(`/instructor/c/${courseId}/packs/${p.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Création refusée.");
    }
  };

  return (
    <section style={styles.block}>
      <div style={styles.contentHead}>
        <span style={styles.label}>Packs de jeu (gamelib)</span>
      </div>
      <p style={styles.hint}>
        Code Python partagé entre les étapes du cours — monté à l'exécution dans
        <code> /lib/pymods/&lt;nom&gt;/</code> pour <code>import &lt;nom&gt;</code>.
        L'élève n'y a jamais accès en lecture.
      </p>

      {packs === null ? (
        <Skeleton height={60} />
      ) : packs.length === 0 ? (
        <p style={styles.empty}>Aucun pack — crées-en un ci-dessous.</p>
      ) : (
        <div style={styles.serieList}>
          {packs.map((p) => (
            <button key={p.id} type="button" className="pl-serie-row" style={styles.serieRow}
                    onClick={() => navigate(`/instructor/c/${courseId}/packs/${p.id}`)}>
              <Icon name="panel" size={15} style={{ color: "var(--blue)", flexShrink: 0 }} />
              <span style={styles.serieTitle}>{p.name}</span>
              <span style={{ flex: 1 }} />
              <span style={styles.serieMeta}>{p.files.length} fichier{p.files.length > 1 ? "s" : ""}</span>
              <Icon name="next" size={14} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}

      <div style={styles.addStudent}>
        <input style={styles.input} placeholder="Nom du pack (ex. aventure)"
               value={name} onChange={(e) => setName(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
        <Button variant="secondary" size="sm" onClick={create} disabled={!name.trim()}>
          + Nouveau pack
        </Button>
      </div>
    </section>
  );
}


function Classes({ courseId, setError, onChanged }: { courseId: number; setError: (s: string | null) => void; onChanged: () => void }) {
  const [classes, setClasses] = useState<CourseClassOption[]>([]);
  const reload = () => listCourseClasses(courseId).then(setClasses).catch(() => {});
  useEffect(() => { reload(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (cl: CourseClassOption) => {
    setError(null);
    try {
      await (cl.assigned ? unassignClass(courseId, cl.id) : assignClass(courseId, cl.id));
      reload(); onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : "Erreur."); }
  };

  return (
    <section style={styles.block}>
      <span style={styles.label}>Classes ayant accès</span>
      {classes.length === 0 ? (
        <p style={styles.empty}>
          Aucune classe. Crée tes classes sur{" "}
          <a href="https://compte.orense.ch/admin" target="_blank" rel="noopener" style={styles.link}>compte.orense.ch</a>.
        </p>
      ) : (
        <div style={styles.chipRow}>
          {classes.filter((c) => c.assigned).map((cl) => (
            <span key={cl.id} style={styles.chip}>
              {cl.name}
              <button type="button" style={styles.chipX} title={`Retirer « ${cl.name} »`} onClick={() => toggle(cl)}>×</button>
            </span>
          ))}
          {classes.every((c) => !c.assigned) && <span style={styles.empty}>Aucune classe assignée.</span>}
          {classes.some((c) => !c.assigned) && (
            <select value="" style={styles.addSelect}
                    onChange={(e) => { const cl = classes.find((c) => c.id === Number(e.target.value)); if (cl) toggle(cl); }}>
              <option value="">+ Assigner une classe…</option>
              {classes.filter((c) => !c.assigned).map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
            </select>
          )}
        </div>
      )}
    </section>
  );
}

function Roster({ courseId, setError }: { courseId: number; setError: (s: string | null) => void }) {
  const [students, setStudents] = useState<CourseStudent[] | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [ident, setIdent] = useState("");

  const reload = () => listStudents(courseId).then(setStudents).catch(() => setStudents([]));
  useEffect(() => { reload(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (p: Promise<unknown>) => {
    setError(null);
    try { await p; reload(); }
    catch (e) { setError(e instanceof Error ? e.message : "Erreur."); }
  };

  const add = () => { if (ident.trim()) run(addStudent(courseId, ident.trim())).then(() => setIdent("")); };
  const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <section style={styles.block}>
      <div style={styles.rosterHead}>
        <span style={styles.label}>Élèves inscrits en direct{students ? ` (${students.length})` : ""}</span>
        {sel.size > 0 && (
          <button style={styles.bulkBtn} type="button" onClick={() => run(removeStudentsBulk(courseId, [...sel])).then(() => setSel(new Set()))}>
            <Icon name="trash" size={13} /> Retirer {sel.size} élève{sel.size > 1 ? "s" : ""}
          </button>
        )}
      </div>

      <div style={styles.addStudent}>
        <input style={styles.input} placeholder="Ajouter un élève (nom ou email)" value={ident}
               onChange={(e) => setIdent(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
        <Button variant="secondary" size="sm" onClick={add}>Ajouter</Button>
      </div>

      {students === null ? (
        <Skeleton height={120} />
      ) : students.length === 0 ? (
        <p style={styles.empty}>Aucun élève inscrit en direct (les membres des classes assignées ont aussi accès).</p>
      ) : (
        <div style={styles.studentList}>
          {students.map((s) => (
            <label key={s.id} style={styles.studentRow}>
              <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} />
              <span style={styles.studentName}>{s.username}</span>
              <span style={styles.studentEmail}>{s.email}</span>
            </label>
          ))}
        </div>
      )}
    </section>
  );
}

function DragDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {[6, 12, 18].flatMap((y) => [8, 16].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 860, display: "flex", flexDirection: "column", gap: 18 },
  back: { display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)", textDecoration: "none", fontSize: 13, fontWeight: 600 },
  error: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--err-box-bg)", color: "var(--red)", borderRadius: "var(--radius)", fontSize: 13 },

  header: { display: "flex", alignItems: "center", gap: 12 },
  dot: { width: 14, height: 14, borderRadius: "50%", flexShrink: 0 },
  titleInput: { background: "transparent", border: "1px solid transparent", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 22, fontWeight: 700, padding: "4px 8px", fontFamily: "inherit", minWidth: 200 },
  status: { fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap", flexShrink: 0 },
  statusActive: { background: "var(--ok-box-bg)", color: "var(--green)" },
  statusArchived: { background: "var(--bg-bar)", color: "var(--text-muted)", border: "1px solid var(--border)" },
  iconBtn: { background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text-muted)", cursor: "pointer", padding: 7, display: "inline-flex" },

  tabs: { display: "flex", gap: 4, borderBottom: "1px solid var(--border-soft)" },
  tabBtn: { background: "transparent", border: "none", borderBottom: "2px solid transparent", color: "var(--text-muted)", cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600, marginBottom: -1 },
  tabBtnOn: { color: "var(--blue)", borderBottomColor: "var(--blue)" },
  actionsRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  dangerZone: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 12, padding: "12px 14px", border: "1px solid var(--red)", borderRadius: "var(--radius)", background: "var(--err-box-bg)" },
  dangerTitle: { fontSize: 13.5, fontWeight: 700, color: "var(--red)" },
  dangerHint: { fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 },
  dangerBtn: { display: "inline-flex", alignItems: "center", gap: 6, background: "var(--red)", color: "var(--on-accent)", border: "none", borderRadius: "var(--radius)", padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, flexShrink: 0 },
  contentHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  cockpitHint: { fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.4 },
  groupList: { display: "flex", flexDirection: "column", gap: 14 },
  chapterHead: { display: "flex", alignItems: "baseline", gap: 8, padding: "0 2px 6px" },
  chapterTitle: { fontSize: 13.5, fontWeight: 700, color: "var(--text)" },
  chapterCount: { fontSize: 12, color: "var(--text-muted)", flexShrink: 0 },
  // Cockpit chapitre
  chapterRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 2px 8px" },
  chHandle: { background: "transparent", border: "none", color: "var(--text-faint)", cursor: "grab", padding: 2, display: "inline-flex", touchAction: "none", flexShrink: 0 },
  chNum: { width: 22, height: 22, borderRadius: "50%", background: "var(--badge-bg)", color: "var(--blue)", fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  chTitleText: { flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, color: "var(--text)", cursor: "text", padding: "2px 4px", borderRadius: "var(--radius-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  chTitleInput: { flex: 1, minWidth: 0, background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 14.5, fontWeight: 700, padding: "3px 6px", fontFamily: "inherit" },
  chEmpty: { fontSize: 12.5, color: "var(--text-faint)", fontStyle: "italic", margin: "2px 0 0 30px" },
  serieList: { display: "flex", flexDirection: "column", gap: 4 },
  serieRow: { display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border-soft)", background: "transparent" },
  serieNav: { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, padding: "6px 2px", background: "transparent", border: "none", cursor: "pointer", color: "var(--text)", fontFamily: "inherit", textAlign: "left", fontSize: 14 },
  eyeBtn: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4, display: "inline-flex", borderRadius: "var(--radius-sm)", flexShrink: 0 },
  rowHidden: { opacity: 0.5 },
  serieTitle: { fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  draftBadge: { fontSize: 10.5, fontWeight: 700, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 999, padding: "1px 7px", flexShrink: 0 },
  serieMeta: { fontSize: 12.5, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums", flexShrink: 0 },

  block: { display: "flex", flexDirection: "column", gap: 10, background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", padding: "16px 18px" },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-muted)" },
  colorRow: { display: "flex", alignItems: "center", gap: 8 },
  swatch: { width: 20, height: 20, borderRadius: "50%", border: "none", cursor: "pointer", outlineOffset: 2 },

  codeLine: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  codeBig: { fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 22, letterSpacing: 2, color: "var(--text)" },
  smallBtn: { display: "inline-flex", alignItems: "center", gap: 4, background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text-muted)", padding: "4px 9px", cursor: "pointer", fontSize: 12 },
  hint: { fontSize: 12, color: "var(--text-muted)" },

  chipRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: 999, padding: "4px 6px 4px 12px", fontSize: 13, color: "var(--text)" },
  chipX: { background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 2px" },
  addSelect: { background: "var(--bg-base)", border: "1px dashed var(--border)", borderRadius: 999, color: "var(--text-muted)", fontSize: 13, padding: "5px 10px", cursor: "pointer", fontFamily: "inherit" },
  link: { color: "var(--blue)" },

  rosterHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  addStudent: { display: "flex", gap: 8 },
  input: { flex: 1, background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", color: "var(--text)", fontSize: 13.5, padding: "8px 10px", fontFamily: "inherit" },
  bulkBtn: { display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "1px solid var(--red)", color: "var(--red)", borderRadius: "var(--radius-sm)", padding: "4px 10px", cursor: "pointer", fontSize: 12.5 },
  studentList: { display: "flex", flexDirection: "column", gap: 2, maxHeight: 360, overflowY: "auto", border: "1px solid var(--border-soft)", borderRadius: "var(--radius)", padding: 6 },
  studentRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", borderRadius: "var(--radius-sm)", fontSize: 13.5, cursor: "pointer" },
  studentName: { fontWeight: 600, color: "var(--text)" },
  studentEmail: { color: "var(--text-muted)", fontSize: 12.5 },

  empty: { color: "var(--text-faint)", fontSize: 13.5, fontStyle: "italic", margin: 0 },

  // Dropzone import bundle JSON
  bundleHead: { display: "flex", alignItems: "center", gap: 10 },
  dropzone: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "22px 16px", border: "2px dashed var(--border)", borderRadius: "var(--radius)", background: "var(--bg-base)", cursor: "pointer", color: "var(--text-muted)", fontSize: 13.5 },
  dropzoneOn: { borderColor: "var(--blue)", background: "var(--badge-bg)", color: "var(--blue)" },
  dropzoneBusy: { opacity: 0.6, cursor: "progress" },
  dropzoneText: { fontWeight: 600 },
  // Bandeaux résultat (sous le dropzone, donc toujours dans le champ visuel)
  bannerOk:  { display: "flex", gap: 10, padding: "10px 14px", borderRadius: "var(--radius)", background: "var(--ok-box-bg)",  border: "1px solid var(--green)", color: "var(--text)", fontSize: 13 },
  bannerErr: { display: "flex", gap: 10, padding: "10px 14px", borderRadius: "var(--radius)", background: "var(--err-box-bg)", border: "1px solid var(--red)",   color: "var(--text)", fontSize: 13 },
  bannerTitle: { fontWeight: 700, marginBottom: 2 },
  bannerBody:  { color: "var(--text-muted)", marginTop: 4 },
  bannerList:  { margin: "6px 0 0 16px", padding: 0, color: "var(--text)" },
  bannerActions: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" },
};
